'use client';

import { Search } from 'lucide-react';
import { useState, useEffect, useRef } from 'react';
import { animated, useTrail } from '@react-spring/web';
import { useScraperStore } from '@/stores/useScraperStore';

interface ScraperSearchBarProps {
  placeholder?: string;
}

export default function ScraperSearchBar({
  placeholder = 'Search scrapers...',
}: ScraperSearchBarProps) {
  const { searchTerm, setSearchTerm } = useScraperStore();
  const [query, setQuery] = useState(searchTerm);
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);

  // Update local state when store changes
  useEffect(() => {
    setQuery(searchTerm);
  }, [searchTerm]);

  const handleInputChange = (value: string) => {
    setQuery(value);

    // Debounce search for typing
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    // Only trigger search after user stops typing for 300ms
    debounceTimerRef.current = setTimeout(() => {
      if (value.trim() && value.trim().length >= 2) {
        setSearchTerm(value.trim());
      } else if (value.trim().length === 0) {
        setSearchTerm('');
      }
    }, 300);
  };

  // Animation with useTrail
  const items = [
    <div key="search-input" className="relative flex-1 group">
      <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground w-5 h-5 group-hover:text-primary transition-colors" />
      <input
        type="text"
        value={query}
        onChange={(e) => handleInputChange(e.target.value)}
        placeholder={placeholder}
        className="w-full h-10 pl-12 pr-4 bg-background rounded-md border border-input 
                 focus:outline-none focus:ring-2 focus:ring-ring focus:border-input
                 transition-all text-base"
        maxLength={50}
      />
    </div>,
  ];

  const trail = useTrail(items.length, {
    from: { opacity: 0, transform: 'translateY(20px)' },
    to: { opacity: 1, transform: 'translateY(0px)' },
    config: { mass: 1, tension: 280, friction: 20 },
  });

  const AnimatedDiv = animated.div as any;

  return (
    <div className="w-full">
      <div className="space-y-5">
        {/* Render items with trail animation */}
        <div className="flex flex-col sm:flex-row gap-3 lg:gap-4">
          {trail.map((style, index) => (
            <AnimatedDiv key={index} style={style} className="flex-1">
              {items[index]}
            </AnimatedDiv>
          ))}
        </div>
      </div>
    </div>
  );
}
